import React, { useState } from "react";
import { Box, Grid, Stack, useTheme, FormControl, InputLabel, Select, MenuItem, Typography } from "@mui/material";
import { LineChart, VerticalBarChart } from "../components/charts";
import { DashboardCard } from "../components/DashboardCard";
import { comparisonMetrics, comparisonTrends, comparisonByDepartment } from "../data";

export const MeasureComparison: React.FC = () => {
  const theme = useTheme();
  const [selectedMetric, setSelectedMetric] = useState("recovery_score");

  const metricKeyMap: Record<string, string> = {
    "Recovery Score": "recovery_score",
    "HRV": "hrv",
    "Resting Heart Rate": "resting_heart_rate",
    "Sleep Performance": "sleep_performance",
    "Sleep Efficiency": "sleep_efficiency",
    "Day Strain": "day_strain",
    "Calories Burned": "calories_burn",
    "Stress Score": "stress_score",
    "Sleep Debt": "sleep_debt",
    "Respiratory Rate": "respiratory_rate",
    "SpO2": "spo2",
  };

  const deptBarKeys = ["recovery_score", "hrv", "sleep_performance", "day_strain"];
  const deptBarLabels: Record<string, string> = {
    recovery_score: "Recovery %",
    hrv: "HRV (ms)",
    sleep_performance: "Sleep %",
    day_strain: "Day Strain",
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Select Metric</InputLabel>
            <Select value={selectedMetric} label="Select Metric" onChange={(e) => setSelectedMetric(e.target.value)}>
              {comparisonMetrics.map((m) => (
                <MenuItem key={m} value={metricKeyMap[m] || m.toLowerCase().replace(/ /g, "_")}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard title="Metric Trend Over Time" subtitle={`Tracking ${comparisonMetrics.find((m) => metricKeyMap[m] === selectedMetric) || selectedMetric} over the last 30 days.`}>
              <LineChart
                data={comparisonTrends}
                xAxisKey="label"
                series={[{ dataKey: selectedMetric, label: comparisonMetrics.find((m) => metricKeyMap[m] === selectedMetric) || selectedMetric, color: theme.palette.primary.main, area: true }]}
                height={350}
                yAxisMin={0}
                yAxisMax={selectedMetric === "recovery_score" || selectedMetric === "sleep_performance" ? 100 : undefined}
              />
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard title="Department Comparison" subtitle="Key metrics compared across departments.">
              <Box sx={{ overflowX: "auto" }}>
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center", py: 2 }}>
                  {deptBarKeys.map((key) => (
                    <Box key={key} sx={{ flex: "1 1 220px", maxWidth: 320, minWidth: 200 }}>
                      <Typography variant="subtitle2" fontWeight={600} textAlign="center" sx={{ mb: 1 }}>{deptBarLabels[key]}</Typography>
                      <VerticalBarChart
                        data={comparisonByDepartment}
                        xAxisKey="department"
                        yAxisKey={key}
                        series={[{ dataKey: key, label: deptBarLabels[key], color: key === "recovery_score" ? theme.palette.success.main : key === "hrv" ? theme.palette.primary.main : key === "sleep_performance" ? theme.palette.info.main : theme.palette.warning.main }]}
                        height={250}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard title="Multi-Metric Trend Overlay" subtitle="Simultaneous comparison of recovery, HRV, resting HR, and sleep performance.">
              <LineChart
                data={comparisonTrends}
                xAxisKey="label"
                series={[
                  { dataKey: "recovery_score", label: "Recovery Score", color: theme.palette.success.main },
                  { dataKey: "hrv", label: "HRV", color: theme.palette.primary.main },
                  { dataKey: "resting_heart_rate", label: "Resting HR", color: theme.palette.warning.main },
                  { dataKey: "sleep_performance", label: "Sleep Performance", color: theme.palette.info.main },
                ]}
                height={350}
                yAxisMin={0}
                yAxisMax={100}
              />
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
